import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type {
  PinCompareActions,
  PinCompareState,
} from "./hooks/usePinCompareState";
import { MAX_COMPARE_POINTS } from "./hooks/usePinCompareState";
import type { SnapshotItem, TooltipSnapshot } from "./utils/tooltipSnapshot";
import { compareLegends } from "./utils/legendUtils";
import { isUnchanged, rowDeltaPct } from "./utils/compareSummary";

interface Props {
  state: PinCompareState;
  actions: PinCompareActions;
  containerEl?: HTMLElement | null;
  customLegendOrder?: string[];
  fontSize: number;
}

interface Row {
  key: string;
  displayName: string;
  color: string;
  unit: string;
  values: (number | null)[];
  formattedValues: (string | undefined)[];
}

interface CompactMeta {
  line1: string;
  line2: string;
}

const COLUMN_LABELS = ["A", "B", "C", "D", "E", "F"];

function buildRows(
  points: TooltipSnapshot[],
  customLegendOrder?: string[],
): Row[] {
  const slotCount = points.length;
  const map = new Map<string, Row>();

  const upsert = (item: SnapshotItem, slot: number) => {
    let row = map.get(item.seriesName);
    if (!row) {
      row = {
        key: item.seriesName,
        displayName: item.displayName,
        color: item.color,
        unit: item.unit,
        values: new Array(slotCount).fill(null),
        formattedValues: new Array(slotCount).fill(undefined),
      };
      map.set(item.seriesName, row);
    }
    row.values[slot] = item.value;
    row.formattedValues[slot] = item.formattedValue;
  };

  points.forEach((snap, slot) => {
    snap.items.forEach((item) => upsert(item, slot));
  });

  const rows = Array.from(map.values());
  rows.sort((a, b) => compareLegends(a.key, b.key, customLegendOrder));
  return rows;
}

function fmtVal(value: number | null): string {
  if (value === null || value === undefined || !isFinite(value)) return "--";
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function deltaDirection(pct: number | null): "up" | "down" | "flat" | "none" {
  if (pct === null) return "none";
  if (isUnchanged(pct)) return "flat";
  return pct > 0 ? "up" : "down";
}

function stripLabelPrefix(text: string, label: string): string {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text.replace(new RegExp(`^${escaped}\\s*[-·]\\s*`, "i"), "").trim();
}

function compactMeta(label: string, snap: TooltipSnapshot): CompactMeta {
  const raw = String(snap.title ?? "");
  const slotRange = raw.match(/^(.+?)\s*-\s*(\d+)\s*\(([^)]+)\)\s*$/);
  if (slotRange) {
    const timeRange = stripLabelPrefix(slotRange[1].trim(), label);
    return {
      line1: `${slotRange[3].trim()}`,
      line2: `${timeRange} · Block ${slotRange[2].trim()}`,
    };
  }
  const rangeOnly = raw.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (rangeOnly) {
    const body = stripLabelPrefix(rangeOnly[1].trim(), label);
    return {
      line1: rangeOnly[2].trim(),
      line2: body,
    };
  }
  return {
    line1: String(snap.xName ?? "").trim(),
    line2: stripLabelPrefix(raw, label),
  };
}

export function PinnedTooltipOverlay({
  state,
  actions,
  containerEl,
  customLegendOrder,
  fontSize,
}: Props) {
  const points = state.comparePoints;
  const referenceIndex = useMemo(
    () =>
      points.findIndex((point) => point.dataIndex === state.referenceDataIndex),
    [points, state.referenceDataIndex],
  );
  const referencePoint = referenceIndex >= 0 ? points[referenceIndex] : null;

  const rows = useMemo(
    () => buildRows(points, customLegendOrder),
    [points, customLegendOrder],
  );

  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
  } | null>(null);
  const resizeRef = useRef<{ startY: number; baseH: number } | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingExiting, setOnboardingExiting] = useState(false);
  const onboardingExitTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const prevPointKeysRef = useRef<Set<number>>(new Set());
  const [newPointKeys, setNewPointKeys] = useState<Set<number>>(new Set());

  useEffect(() => {
    const prev = prevPointKeysRef.current;
    const currentKeys = new Set(points.map((p) => p.dataIndex));
    const added = new Set<number>();
    currentKeys.forEach((k) => {
      if (!prev.has(k)) added.add(k);
    });
    prevPointKeysRef.current = currentKeys;
    if (added.size === 0) return;
    setNewPointKeys(added);
    const t = setTimeout(() => setNewPointKeys(new Set()), 500);
    return () => clearTimeout(t);
  }, [points]);

  useEffect(() => {
    if (!state.isCompareMode) {
      setOnboardingExiting(true);
      onboardingExitTimer.current = setTimeout(() => {
        setShowOnboarding(false);
        setOnboardingExiting(false);
      }, 320);
      return () => {
        if (onboardingExitTimer.current)
          clearTimeout(onboardingExitTimer.current);
      };
    }
    if (onboardingExitTimer.current) clearTimeout(onboardingExitTimer.current);
    setOnboardingExiting(false);
    setShowOnboarding(true);
    const timer = setTimeout(() => {
      setOnboardingExiting(true);
      onboardingExitTimer.current = setTimeout(() => {
        setShowOnboarding(false);
        setOnboardingExiting(false);
      }, 320);
    }, 4500);
    return () => {
      clearTimeout(timer);
      if (onboardingExitTimer.current)
        clearTimeout(onboardingExitTimer.current);
    };
  }, [state.activationTick, state.isCompareMode]);

  useEffect(() => {
    if (!state.isCompareMode) setPos(null);
  }, [state.isCompareMode]);

  const onHeaderMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if ((event.target as HTMLElement).closest("button")) return;
      event.preventDefault();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const rect = overlayRef.current?.getBoundingClientRect();
      const base = rect ? { x: rect.left, y: rect.top } : pos ?? { x: vw - 436, y: 8 };
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        baseX: base.x,
        baseY: base.y,
      };

      const onMove = (moveEvent: MouseEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const nextX = drag.baseX + (moveEvent.clientX - drag.startX);
        const nextY = drag.baseY + (moveEvent.clientY - drag.startY);
        setPos({
          x: Math.max(0, Math.min(vw - 80, nextX)),
          y: Math.max(0, Math.min(vh - 40, nextY)),
        });
      };

      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [pos],
  );

  const onResizeMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const currentH = overlayRef.current?.getBoundingClientRect().height ?? window.innerHeight * 0.5;
    resizeRef.current = { startY: event.clientY, baseH: currentH };

    const onMove = (moveEvent: MouseEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      const delta = moveEvent.clientY - r.startY;
      const minH = 120;
      const maxH = window.innerHeight * 0.9;
      setCardHeight(Math.max(minH, Math.min(maxH, r.baseH + delta)));
    };

    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  if (!state.isCompareMode || !referencePoint) return null;

  const overlayStyle: React.CSSProperties = {
    ["--remix-pin-fs" as any]: `${fontSize}px`,
    ["--remix-pin-vals" as any]: String(points.length),
    position: "fixed",
    zIndex: 9999,
    ...(cardHeight != null ? { maxHeight: cardHeight } : {}),
    ...(pos
      ? { top: pos.y, left: pos.x, right: "auto" }
      : { top: 8, right: 16, left: "auto" }),
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="remix-pin-overlay"
      style={overlayStyle}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="remix-pin-card">
        <div className="remix-pin-header" onMouseDown={onHeaderMouseDown}>
          <span
            className="remix-pin-grip"
            aria-hidden="true"
            title="Drag to move"
          >
            <span className="remix-pin-grip-bar" />
            <span className="remix-pin-grip-bar" />
            <span className="remix-pin-grip-bar" />
          </span>
          <span className="remix-pin-title">COMPARE</span>
          <span className="remix-pin-points-badge">
            {points.length}/{MAX_COMPARE_POINTS} points
          </span>
          <span className="remix-pin-mode-chip">Double-click sets first</span>
          <span className="remix-pin-header-spacer" />
          <button
            type="button"
            className="remix-pin-close"
            aria-label="Clear compare mode"
            title="Clear compare mode"
            onClick={() => actions.clearCompareMode()}
          >
            ×
          </button>
        </div>

        {showOnboarding && (
          <div
            className={`remix-compare-onboarding${onboardingExiting ? " exiting" : ""}`}
            role="status"
            aria-live="polite"
          >
            Compare mode enabled · Click points to compare · Esc to clear
          </div>
        )}

        <div className="remix-pin-body has-points">
          <div className="remix-pin-col-head">
            <div className="remix-pin-col-name">
              <span>Series</span>
            </div>

            {points.map((snap, idx) => {
              const label = COLUMN_LABELS[idx] ?? `P${idx + 1}`;
              const meta = compactMeta(label, snap);
              const isReference = idx === referenceIndex;
              return (
                <div
                  key={`head-${snap.dataIndex}-${idx}`}
                  className={`remix-pin-col-val${isReference ? " is-reference" : ""}${newPointKeys.has(snap.dataIndex) ? " point-added" : ""}`}
                  role="button"
                  tabIndex={0}
                  title={
                    isReference
                      ? "Reference point"
                      : `Pin ${label} as reference`
                  }
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("button")) return;
                    actions.setReferencePoint(snap);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      actions.setReferencePoint(snap);
                    }
                  }}
                  style={{ cursor: isReference ? "default" : "pointer" }}
                >
                  <div
                    className="remix-pin-col-sub compact"
                    title={String(snap.title)}
                  >
                    <span className="remix-pin-col-sub-inline">
                      <span className="remix-pin-col-letter">{label}</span>
                      {isReference && (
                        <span
                          className="remix-pin-first-badge is-active"
                          title="Reference point"
                          aria-label="Reference"
                        >
                          📌
                        </span>
                      )}
                      {meta.line1 && (
                        <span className="remix-pin-col-sub-time ml-2">
                          {meta.line1}
                        </span>
                      )}
                      <button
                        type="button"
                        className="remix-pin-col-remove"
                        aria-label={`Remove ${label}`}
                        title={`Remove ${label}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          actions.removePoint(snap.dataIndex);
                        }}
                      >
                        ×
                      </button>
                    </span>
                    <span>{meta.line2}</span>
                  </div>
                </div>
              );
            })}

            <div className="remix-pin-col-delta">
              Change vs {COLUMN_LABELS[referenceIndex] ?? "First"}
            </div>
          </div>

          {rows.map((row) => {
            const referenceValue = row.values[referenceIndex] ?? null;
            return (
              <div
                key={row.key}
                className="remix-pin-row"
                style={{ ["--accent" as any]: row.color }}
              >
                <span className="remix-pin-dot" />
                <span className="remix-pin-name-wrap" title={row.displayName}>
                  <span className="remix-pin-name">{row.displayName}</span>
                </span>

                {row.values.map((value, idx) => {
                  const formatted = row.formattedValues[idx];
                  return (
                    <span
                      key={`value-${row.key}-${idx}`}
                      className="remix-pin-val"
                    >
                      {formatted ?? fmtVal(value)}
                      {row.unit && !formatted ? (
                        <span className="remix-pin-unit">{row.unit}</span>
                      ) : null}
                    </span>
                  );
                })}

                <span className="remix-pin-delta multi">
                  {points.map((_, idx) => {
                    if (idx === referenceIndex) return null;
                    const pct = rowDeltaPct(
                      referenceValue,
                      row.values[idx] ?? null,
                    );
                    const dir = deltaDirection(pct);
                    const label = COLUMN_LABELS[idx] ?? `P${idx + 1}`;
                    if (pct === null) {
                      return (
                        <span
                          key={`delta-${row.key}-${idx}`}
                          className="remix-pin-delta-chip dir-none"
                        >
                          {label}: --
                        </span>
                      );
                    }
                    const sign = dir === "up" ? "+" : dir === "down" ? "-" : "";
                    return (
                      <span
                        key={`delta-${row.key}-${idx}`}
                        className={`remix-pin-delta-chip dir-${dir}`}
                      >
                        {label}: {sign}
                        {Math.abs(pct).toFixed(1)}%
                      </span>
                    );
                  })}
                </span>
              </div>
            );
          })}
        </div>
        <div
          className="remix-pin-resize-handle"
          onMouseDown={onResizeMouseDown}
          title="Drag to resize"
          aria-hidden="true"
        />
      </div>
    </div>,
    document.body,
  );
}
